import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, List, ListItem, Spinner, Text, useToast } from "@chakra-ui/react";
import { ArrowBackIcon, ChevronLeftIcon, ChevronRightIcon, RepeatIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import rbacService from "../services/rbacService";
import type { Role, User } from "../../rbac/types";

const UserDetailPage: React.FC = () => {
  const { id } = useParams();
  const userId = id as string; // UUID
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [assignedRoles, setAssignedRoles] = useState<Role[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<string | null>(null);
  const [selectedAssigned, setSelectedAssigned] = useState<string | null>(null);

  const availableRoles = useMemo(() => {
    const assignedIds = new Set(assignedRoles.map(r => r.id));
    return allRoles.filter(r => !assignedIds.has(r.id));
  }, [allRoles, assignedRoles]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, roles, assigned] = await Promise.all([
        rbacService.getUserById(userId),
        rbacService.getRoles({ skip: 0, limit: 1000 }),
        rbacService.getUserRoles(userId),
      ]);
      setUser(u);
      setAllRoles(roles);
      setAssignedRoles(assigned);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat data user", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [userId]);

  const assign = async () => {
    if (selectedAvailable == null) return;
    try {
      await rbacService.assignRoleToUser(userId, selectedAvailable);
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal menambah role", description: e?.response?.data?.detail || e?.message });
    }
  };

  const revoke = async () => {
    if (selectedAssigned == null) return;
    try {
      await rbacService.removeRoleFromUser(userId, selectedAssigned);
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal mencabut role", description: e?.response?.data?.detail || e?.message });
    }
  };

  if (loading) return <Box p={6}><Spinner /></Box>;
  if (!user) return <Box p={6}>User tidak ditemukan</Box>;

  return (
    <Box p={4}>
      <HStack justify="space-between" mb={4}>
        <HStack>
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate(-1)}>Back</Button>
          <Heading size="md">User: {user.email}</Heading>
        </HStack>
        <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} />
      </HStack>

      <Flex gap={6}>
        <Box flex={1}>
          <Heading size="sm" mb={2}>Available Roles</Heading>
          <Box borderWidth="1px" borderRadius="md" p={2} maxH="400px" overflowY="auto">
            <List spacing={1}>
              {availableRoles.map(r => (
                <ListItem key={r.id} p={2} borderRadius="md" bg={selectedAvailable === r.id ? "green.50" : "transparent"} onClick={() => setSelectedAvailable(r.id)} cursor="pointer">
                  <Text fontSize="sm">{r.role_name} {r.app_id == null ? "(Global)" : `(App ${r.app_id})`}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Flex direction="column" align="center" justify="center" gap={2}>
          <IconButton aria-label="assign" icon={<ChevronRightIcon />} onClick={assign} isDisabled={selectedAvailable == null} />
          <IconButton aria-label="revoke" icon={<ChevronLeftIcon />} onClick={revoke} isDisabled={selectedAssigned == null} />
        </Flex>

        <Box flex={1}>
          <Heading size="sm" mb={2}>Assigned Roles</Heading>
          <Box borderWidth="1px" borderRadius="md" p={2} maxH="400px" overflowY="auto">
            <List spacing={1}>
              {assignedRoles.map(r => (
                <ListItem key={r.id} p={2} borderRadius="md" bg={selectedAssigned === r.id ? "red.50" : "transparent"} onClick={() => setSelectedAssigned(r.id)} cursor="pointer">
                  <Text fontSize="sm">{r.role_name} {r.app_id == null ? "(Global)" : `(App ${r.app_id})`}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default UserDetailPage;
